// src/pages/Transactions.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getTransactions } from "../redux/slices/transactionSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Search,
  ArrowDownRight,
  ArrowUpRight,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Filter,
  Sparkles,
  ChevronDown,
  TrendingUp,
  BarChart3,
  CreditCard,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFiat } from "../utils/currency";
import LoadingScreen from "../components/LoadingScreen";

const Transactions: React.FC = () => {
  const dispatch = useDispatch();
  const { transactions, isLoading } = useSelector(
    (state: RootState) => state.transactions,
  );
  const [filter, setFilter] = useState({
    type: "",
    status: "",
    search: "",
  });

  useEffect(() => {
    dispatch(getTransactions() as any);
  }, [dispatch]);

  const filteredTransactions = transactions.filter((transaction) => {
    if (filter.type && transaction.type !== filter.type) return false;
    if (filter.status && transaction.status !== filter.status) return false;
    if (filter.search) {
      const searchLower = filter.search.toLowerCase();
      return (
        transaction.tx_hash?.toLowerCase().includes(searchLower) ||
        transaction.wallet_address?.toLowerCase().includes(searchLower) ||
        transaction.type.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const handleRefresh = () => {
    dispatch(getTransactions() as any);
    toast.success("Transactions refreshed");
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "confirmed":
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case "pending":
        return <Clock className="h-4 w-4 text-amber-500 animate-pulse" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-rose-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800";
      case "pending":
        return "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800";
      case "rejected":
        return "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800";
      default:
        return "bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-800";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "deposit":
        return "bg-emerald-500/10 text-emerald-500";
      case "withdrawal":
        return "bg-rose-500/10 text-rose-500";
      case "bonus":
        return "bg-blue-500/10 text-blue-500";
      default:
        return "bg-primary/10 text-primary";
    }
  };

  // Calculate stats
  const totalTransactions = transactions.length;
  const totalDeposits = transactions
    .filter((t) => t.type === "deposit" && t.status === "confirmed")
    .reduce((sum, t) => sum + t.amount, 0);
  const totalWithdrawals = transactions
    .filter((t) => t.type === "withdrawal" && t.status === "confirmed")
    .reduce((sum, t) => sum + t.amount, 0);
  const pendingTransactions = transactions.filter(
    (t) => t.status === "pending",
  ).length;

  if (isLoading) {
    return <LoadingScreen message="Loading transactions..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-6 border border-primary/10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-primary/20 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
        <div className="relative">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <CreditCard className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Transaction History
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Track all your transaction history and activity
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} group/refresh`}
            >
              <RefreshCw className="h-4 w-4 mr-2 transition-transform group-hover/refresh:rotate-180 duration-500" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`${GlobalStyles.card} !p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total Transactions
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {totalTransactions}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
              <BarChart3 className="h-5 w-5 text-primary" />
            </div>
          </div>
        </div>

        <div className={`${GlobalStyles.card} !p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total Deposits
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatFiat(totalDeposits)}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500/20 to-emerald-400/10">
              <ArrowDownRight className="h-5 w-5 text-emerald-500" />
            </div>
          </div>
        </div>

        <div className={`${GlobalStyles.card} !p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total Withdrawals
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatFiat(totalWithdrawals)}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-gradient-to-br from-rose-500/20 to-rose-400/10">
              <ArrowUpRight className="h-5 w-5 text-rose-500" />
            </div>
          </div>
        </div>

        <div className={`${GlobalStyles.card} !p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Pending
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {pendingTransactions}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-400/10">
              <Clock className="h-5 w-5 text-amber-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`${GlobalStyles.card}`}>
        <div className="flex items-center gap-2 mb-6">
          <Filter className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Filter Transactions
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="group">
            <label
              className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
            >
              <span>Type</span>
              <Sparkles className="h-3 w-3 text-primary" />
            </label>
            <div className="relative">
              <select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className={`${GlobalStyles.input} appearance-none pr-8`}
              >
                <option value="">All Types</option>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="bonus">Bonus</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="group">
            <label className={`${GlobalStyles.inputLabel}`}>Status</label>
            <div className="relative">
              <select
                value={filter.status}
                onChange={(e) =>
                  setFilter({ ...filter, status: e.target.value })
                }
                className={`${GlobalStyles.input} appearance-none pr-8`}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="rejected">Rejected</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="group">
            <label
              className={`${GlobalStyles.inputLabel} flex items-center gap-1`}
            >
              <span>Search</span>
              <Search className="h-3 w-3 text-primary" />
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                placeholder="Search by hash, wallet, or type..."
                value={filter.search}
                onChange={(e) =>
                  setFilter({ ...filter, search: e.target.value })
                }
                className={`${GlobalStyles.input} pl-10`}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-primary animate-pulse"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-white">
                {filteredTransactions.length}
              </span>{" "}
              transactions found
            </p>
          </div>
          <div className="flex gap-2">
            {(filter.type || filter.status || filter.search) && (
              <button
                onClick={() => setFilter({ type: "", status: "", search: "" })}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall} group/clear`}
              >
                <span>Clear Filters</span>
                <Sparkles className="h-3 w-3 ml-1 opacity-0 group-hover/clear:opacity-100 transition-opacity" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className={`${GlobalStyles.card} !p-0 overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className={`${GlobalStyles.table} !border-0`}>
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Transaction Hash
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Date & Time
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors group/row"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className={`p-2 rounded-lg ${getTypeColor(transaction.type)}`}
                        >
                          {transaction.type === "deposit" ? (
                            <ArrowDownRight className="h-4 w-4" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4" />
                          )}
                        </div>
                        <span className="ml-3 font-medium capitalize text-gray-900 dark:text-white">
                          {transaction.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`text-lg font-semibold ${
                          transaction.type === "deposit"
                            ? "text-emerald-500"
                            : transaction.type === "withdrawal"
                              ? "text-rose-500"
                              : "text-blue-500"
                        }`}
                      >
                        {transaction.type === "deposit" ? "+" : "-"}
                        {formatFiat(transaction.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(transaction.status)}
                        <span
                          className={`ml-2 text-xs font-medium px-2.5 py-1 rounded-full border ${getStatusColor(transaction.status)}`}
                        >
                          {transaction.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className="font-mono text-sm text-gray-600 dark:text-gray-400 group-hover/row:text-gray-800 dark:group-hover/row:text-gray-200 transition-colors"
                        title={transaction.tx_hash || "N/A"}
                      >
                        {transaction.tx_hash ? (
                          <>
                            <span className="text-primary/60">
                              {transaction.tx_hash.slice(0, 16)}...
                            </span>
                            <span className="text-gray-400 dark:text-gray-500">
                              {transaction.tx_hash.slice(-16)}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">
                            N/A
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(transaction.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            },
                          )}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {new Date(transaction.created_at).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12">
                    <div className="text-center">
                      <div className="relative mx-auto w-20 h-20 mb-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-full blur-lg"></div>
                        <div className="relative w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center backdrop-blur-sm border border-gray-200 dark:border-gray-800">
                          <Search className="h-8 w-8 text-gray-400" />
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        No transactions found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                        {transactions.length === 0
                          ? "Start by making your first transaction to see it here"
                          : "Try adjusting your filters or search terms"}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State Helper */}
      {transactions.length === 0 && (
        <div className={`${GlobalStyles.card}`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                <AlertCircle className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  Get Started with Transactions
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Make your first deposit or withdrawal to see transaction
                  history here
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href="/deposit"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} group/deposit`}
              >
                <ArrowDownRight className="h-4 w-4 mr-2 transition-transform group-hover/deposit:-translate-y-0.5" />
                <span>Make Deposit</span>
              </a>
              <a
                href="/withdraw"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} group/withdraw`}
              >
                <ArrowUpRight className="h-4 w-4 mr-2 transition-transform group-hover/withdraw:-translate-y-0.5" />
                <span>Request Withdrawal</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
